import { Fragment } from 'react'
import { Check, Phone, Sparkles, Users, MessageSquare, Plug, Headset } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { COMPARISON_CATEGORIES_FR, COMPARISON_PLAN_ORDER } from './comparisonData'

const PLAN_NAMES: Record<string, string> = {
  solo: 'Solo',
  team: 'Team',
  business: 'Business',
  premium: 'Premium',
  enterprise: 'Enterprise',
}

const TEAM_INDEX = 1

const SECTION_ICONS: Record<string, LucideIcon> = {
  phone: Phone,
  sparkle: Sparkles,
  users: Users,
  chat: MessageSquare,
  plug: Plug,
  headset: Headset,
}

/** Logos affichés devant une ligne, repérés par un mot du libellé. */
const ROW_LOGOS: ReadonlyArray<[RegExp, string[]]> = [
  [/Clio/i, ['clio']],
  [/Google Contacts/i, ['google-contacts']],
  [/HubSpot/i, ['hubspot']],
  [/Pipedrive/i, ['pipedrive']],
  [/Zoho/i, ['zoho']],
  [/Slack/i, ['slack']],
  [/Zapier/i, ['zapier']],
]

function rowLogos(label: string): string[] {
  const hit = ROW_LOGOS.find(([re]) => re.test(label))
  return hit ? hit[1] : []
}

function renderCell(content: string, isHighlight: boolean) {
  if (content === '✓') {
    return (
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
          isHighlight ? 'bg-blue-500 text-white' : 'bg-white/10 text-blue-300'
        }`}
      >
        <Check className="w-3 h-3" />
      </span>
    )
  }
  if (content === '✗') {
    return <span className="text-white/25">—</span>
  }
  return <span className="text-[13.5px] text-white/85">{content}</span>
}

export function PricingComparisonTable() {
  const { t } = useTranslation()
  // Les libellés viennent du même fonds que la page publique callizy.ca :
  // un terme y dit donc exactement la même chose qu'ici, dans les deux
  // langues. COMPARISON_CATEGORIES_FR ne sert plus que de repli.
  const categories = t('pricing.comparison', {
    returnObjects: true,
    defaultValue: COMPARISON_CATEGORIES_FR,
  }) as typeof COMPARISON_CATEGORIES_FR
  const inclus = t('pricing.comparisonIncluded', {
    returnObjects: true,
    defaultValue: { title: 'Inclus dans tous les plans', items: [] as string[] },
  }) as { title: string; items: string[] }

  return (
    <section className="py-20 md:py-24 border-t border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[12px] tracking-[0.14em] uppercase text-blue-400 font-medium mb-3">
            {t('pricing.comparisonEyebrow', 'Comparaison détaillée')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            {t('pricing.comparisonTitle', 'Tous les plans, ligne par ligne')}
          </h2>
        </div>

        <div className="mt-12 -mx-6 lg:-mx-8 overflow-x-auto">
          <table className="min-w-[1000px] w-full text-left border-collapse">
            <thead className="border-b border-white/10">
              <tr>
                <th className="py-5 px-6 lg:px-8 text-[11px] font-medium uppercase tracking-[0.12em] text-white/55 w-[300px]">
                  {t('pricing.comparisonHeaderFeature', 'Fonctionnalité')}
                </th>
                {COMPARISON_PLAN_ORDER.map((slug, i) => {
                  const isTeam = i === TEAM_INDEX
                  return (
                    <th
                      key={slug}
                      className={`py-5 px-5 ${isTeam ? 'bg-blue-500/[0.08]' : ''}`}
                    >
                      <span
                        className={`text-[15px] font-semibold ${
                          isTeam ? 'text-blue-300' : 'text-white'
                        }`}
                      >
                        {PLAN_NAMES[slug]}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {categories.map((g, gi) => (
                <Fragment key={g.name}>
                  <tr>
                    <td
                      colSpan={6}
                      className="bg-white/[0.04] px-6 lg:px-8 py-3 text-[11px] tracking-[0.14em] uppercase font-semibold text-blue-300 border-y border-white/[0.06]"
                    >
                      <span className="inline-flex items-center gap-2">
                        {g.icon && SECTION_ICONS[g.icon]
                          ? (() => {
                              const Icon = SECTION_ICONS[g.icon]
                              return <Icon className="w-4 h-4 text-blue-300/80" />
                            })()
                          : null}
                        {g.name}
                        {g.icon === 'chat' ? (
                          <span className="inline-flex items-center gap-1.5 ml-1">
                            <img src="/integrations/assistants/openai.svg" alt="ChatGPT" className="w-4 h-4 opacity-90" />
                            <img src="/integrations/assistants/claude.svg" alt="Claude" className="w-4 h-4 opacity-90" />
                          </span>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                  {g.rows.map((row, ri) => (
                    <tr
                      key={`${gi}-${ri}`}
                      className="border-b border-white/[0.05] last:border-b-0 hover:bg-white/[0.02]"
                    >
                      <td className="py-3.5 px-6 lg:px-8 text-[14px] text-white/85 align-top">
                        <span className="inline-flex items-center gap-2.5">
                          {(g.icon === 'plug' ? rowLogos(row[0]) : []).map((logo) => (
                            <img
                              key={logo}
                              src={`/integrations/${logo}.svg`}
                              alt=""
                              className="w-5 h-5 rounded-[4px] bg-white p-[2px]"
                            />
                          ))}
                          {row[0]}
                        </span>
                      </td>
                      {row.slice(1).map((v, ci) => (
                        <td
                          key={ci}
                          className={`py-3.5 px-5 align-top ${
                            ci === TEAM_INDEX ? 'bg-blue-500/[0.05]' : ''
                          }`}
                        >
                          {renderCell(v, ci === TEAM_INDEX)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {inclus.items.length > 0 && (
          <div className="mt-14 max-w-6xl mx-auto rounded-3xl ring-1 ring-white/10 bg-white/[0.03] px-6 sm:px-8 py-7">
            <h3 className="text-[17px] font-semibold text-white">{inclus.title}</h3>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2.5">
              {inclus.items.map((ligne) => (
                <li key={ligne} className="flex items-start gap-2.5 text-[14px] text-white/80">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-blue-400 flex-none" aria-hidden />
                  {ligne}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
